import DeleteIcon from '@mui/icons-material/Delete';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import FormDialog from '../components/form';
import GridBase from '../components/grid';
import { useBreadcrumbs } from '../contexts/Breadcrumbs';
import { usePrerequisites } from '../contexts/Prerequisites';
import { useUniqueEffect, useLiveRef } from '../hooks';
import ConfirmDialog from './confirm';
import { deleteRecord, getRecord } from './crud-helper';

const FormBase = ({ modelConfig, parentFilters, parent, api, onSetActiveRecord, canMutateLocation = true, initialValues, onGridDataLoad, tableRef, onCellClick, onGridStartFetchData, onSubmit, ...rest }) => {
	const [{ strings }] = usePrerequisites();
	const navigate = useNavigate();
	const location = useLocation();
	const [activeRecord, setActiveRecord] = useState(undefined);
	const [childFilters, setChildFilters] = useState(null);
	const [refresh, setRefresh] = useState(null);
	const [apiParams, setApiParams] = useState(null);
	const [isLoading, setIsLoading] = useState(false);
	const [isNew, setIsNew] = useState(false);
	const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
	const [{ breadcrumbs }, setBreadcrumbsState] = useBreadcrumbs();
	const touchedRef = useRef(false);
	const [searchParams, setSearchParams] = useSearchParams();
	const idSearchParam = searchParams.has('id') ? searchParams.get('id') : null;
	const modules = useSelector(state => state.auth?.modules|| {});
	const [moduleName, module] = !!modules && typeof modules === 'object' ? Object.entries(modules)?.find?.(([key, module]) => key === modelConfig.name) || [] : [];
	const hasAccess = !module || !modelConfig.name || module?.Module;
	

	const { readOnly, disableDelete } = useMemo(() => {
		return {
			disableDelete:
				typeof modelConfig.disableDelete === 'function'
					? modelConfig.disableDelete({ activeRecord, location })
					: Boolean(modelConfig.disableDelete),
			readOnly:
				typeof modelConfig.readOnly === 'function'
					? modelConfig.readOnly({ activeRecord, location })
					: Boolean(modelConfig.readOnly)
		};
	}, [activeRecord, location]);

	const [searchParamsInitialValues, searchParamsParentFilters] = useMemo(() => {
		let filters = [];
		let initValues = {};
		for (let i = 0; i < modelConfig.columns?.length; i++) {
			if (modelConfig.columns[i].formField && searchParams.has(modelConfig.columns[i].formField)) {
				filters.push({
					operator: '$eq',
					value: searchParams.get(modelConfig.columns[i].formField),
					property: modelConfig.columns[i].formField,
				});
				initValues[modelConfig.columns[i].formField] = searchParams.get(modelConfig.columns[i].formField);
			}
			if (modelConfig.columns[i].field && searchParams.has(modelConfig.columns[i].field)) {
				filters.push({
					operator: '$eq',
					value: searchParams.get(modelConfig.columns[i].field),
					property: modelConfig.columns[i].field,
				});
				initValues[modelConfig.columns[i].field] = searchParams.get(modelConfig.columns[i].field);
			}
		}

		return [initValues, filters.length > 0 ? { filter: filters } : undefined];
	}, [searchParams.toString()])

	const handleOnSetActiveRecord = useCallback(
		(record, opts = { refresh: true }) => {
			const { searchParamsRef } = opts;
			const returnTo = decodeURIComponent(searchParams.get('returnTo') || searchParamsRef?.current.get('returnTo') || '');
			if (returnTo) {
				navigate(`${!returnTo.startsWith('/') ? '/' : ''}${returnTo}`.trim());
				return;
			}
			if (canMutateLocation && !record && idSearchParam) {
				searchParams.delete('id');
				setSearchParams(searchParams);
			}
			if (typeof onSetActiveRecord === 'function') {
				onSetActiveRecord(record, opts);
			} else {
				setActiveRecord(record);
				if (!opts.refresh) return;
				setRefresh(new Date());
			}
		},
		[searchParams, idSearchParam, canMutateLocation]
	);

	const onSelection = useCallback(
		(id) => {
			setChildFilters(id ? { [modelConfig.name + 'Id']: id } : null);
		},
		[modelConfig]
	);

	const openForm = useCallback(
		({ id, ...opts }) => {
			setIsNew(!id);
			getRecord({
				id,
				api: api || modelConfig?.endPoint,
				setIsLoading,
				setActiveRecord,
				apiParams,
				modelConfig,
				parentFilters,
				...opts
			});
		},
		[modelConfig, apiParams, parentFilters]
	);


	const handleOnClickTitleBreadcrub = (e) => {
		setBreadcrumbsState({ breadcrumbs: [] });
		setActiveRecord(undefined);
	};

	const handleOnToggleDeleteConfirmOpen = (open) => () => setDeleteConfirmOpen(open);
	const handleOnDeleteConfirm = useCallback(() => {
		setIsLoading(true);
		setDeleteConfirmOpen(false);
		deleteRecord({ id: activeRecord?.id, api: api || modelConfig?.endPoint }).then((res) => {
			handleOnSetActiveRecord(undefined);
			setBreadcrumbsState({ breadcrumbs: [] });
			setActiveRecord(undefined);
			setIsLoading(false);
		});
	}, [activeRecord?.id]);

	useEffect(() => {
		onSelection(0);
		setApiParams(typeof modelConfig?.apiParams === 'function' ? modelConfig.apiParams({ location, searchParams }) : (modelConfig.apiParams || {}));
	}, []);


	useUniqueEffect(() => {
		if (idSearchParam !== null && canMutateLocation) {
			let id = idSearchParam;
			if ([0, '0', 'new'].includes(idSearchParam)) {
				id = null;
			}
			openForm({ id });
		} else {
			setActiveRecord(null);
		}
	}, [idSearchParam, canMutateLocation]);

	//A Breadcrumb update botch
	useUniqueEffect(() => {
		let formBreadcrumbActions = [];
		let title =
			typeof modelConfig.title === 'function'
				? modelConfig.title({ searchParams, location, activeRecord })
				: modelConfig.title;
		
		const gridTitle =
			typeof modelConfig.gridTitle === 'function'
				? modelConfig.gridTitle({ searchParams, location, activeRecord })
				: modelConfig.gridTitle ?? title;
		if (activeRecord) {
			activeRecord.record.EquipmentConnectivityType = activeRecord?.record?.EquipmentConnectivityType
				? activeRecord?.record?.EquipmentConnectivityType
				: activeRecord?.record?.ConnectivityType;
			if (!touchedRef.current) {
				touchedRef.current = true;
			}
			let formTitle =
				typeof modelConfig?.formTitle === 'function'
					? modelConfig?.formTitle({ searchParams, location, activeRecord })
					: modelConfig?.formTitle;
			formTitle = strings[formTitle] ?? formTitle; 
			
			formTitle = formTitle ? formTitle : activeRecord.id ? 'Update' : 'New';

			if (modelConfig.formTitleField) {
				formTitle =
					typeof activeRecord.record === 'object' && activeRecord.record[modelConfig.formTitleField]
						? activeRecord.record[modelConfig.formTitleField]
						: formTitle;
			}

			if (!disableDelete || modelConfig.showDeleteBtn) {
				formBreadcrumbActions.push({
					text: strings.Delete,
					color: 'error',
					onClick: handleOnToggleDeleteConfirmOpen(true),
					variant: 'contained',
					disabled: disableDelete || !activeRecord?.id,
					size: 'small',
					startIcon: <DeleteIcon />
				});
			}
			if (!modelConfig.skipSetBreadcrumbs) {
				setBreadcrumbsState({
					breadcrumbs: [
						{ text: gridTitle || modelConfig.name, onClick: handleOnClickTitleBreadcrub },
						{ text: formTitle, color: 'text.secondary' }
					],
					actions: formBreadcrumbActions
				});
			}
			
			if(Array.isArray(modelConfig.breadcrumbActions)) {
				formBreadcrumbActions = formBreadcrumbActions.concat(modelConfig.breadcrumbActions);
			}
			
			setBreadcrumbsState({
				breadcrumbs: [
					{ text: gridTitle || modelConfig.name, onClick: handleOnClickTitleBreadcrub },
					{ text: formTitle, color: 'text.secondary' }
				],
				actions: formBreadcrumbActions
			});
		} else if (!!modelConfig.gridTitle && !modelConfig.skipSetBreadcrumbs) {
			setBreadcrumbsState({ breadcrumbs: [{ text: strings[gridTitle] || gridTitle }], actions: [] });
		} else {
			if ((touchedRef.current || breadcrumbs?.length > 0) && !modelConfig.skipSetBreadcrumbs) {
				setBreadcrumbsState({ actions: [] });
			}
		}
		return () => {};
	}, [activeRecord, disableDelete, searchParams, location]);

	// if initial effect not run - do not render anything
	if (apiParams === null) {
		return null;
	}
	return (
		<div id="wrapper ibox float-e-margins">
			<ConfirmDialog
				title={strings.Delete}
				body={strings.DeleteAlertText}
				onCancel={handleOnToggleDeleteConfirmOpen(false)}
				onConfirm={handleOnDeleteConfirm}
				open={deleteConfirmOpen}
			/>
			{idSearchParam || activeRecord ? (
				<FormDialog
					modelConfig={modelConfig}
					apiParams={apiParams}
					activeRecord={activeRecord}
					loading={isLoading}
					parent={parent}
					setActiveRecord={handleOnSetActiveRecord}
					isNew={isNew}
					initialValues={{ ...initialValues, ...searchParamsInitialValues }}
					onSubmit={onSubmit}
				/>
			) : (
				<GridBase
					modelConfig={modelConfig}
					apiParams={apiParams}
					onCellClick={onCellClick}
					setActiveRecord={handleOnSetActiveRecord}
					onSelection={onSelection}
					parentFilters={parentFilters ? parentFilters : searchParamsParentFilters}
					parent={parent}
					refresh={refresh}
					readOnly={readOnly}
					onOpenForm={onSetActiveRecord}
					canMutateLocation={canMutateLocation}
					onDataLoad={onGridDataLoad}
					ref={tableRef}
					onGridStartFetchData={onGridStartFetchData}
					{...rest}
				/>
			)}
		</div>
	);
};

export default FormBase;
